/*
 * assets/js/src/polaroidy.js — galerie polaroidů pod formulářem na HP.
 *
 * Port kompozice showreelu (`zagency-gallery/index.html`), ne volná
 * interpretace efektu z banky. Zadání majitele: „stejná média, stejný
 * motion". Showreel jede na GSAP timeline; šablona GSAP nemá a nepřidá se,
 * takže je to port do WAAPI (`element.animate`) s převedenými křivkami:
 *   back.out(1.7) ≈ cubic-bezier(.34, 1.56, .64, 1)
 *   back.out(2.2) ≈ cubic-bezier(.34, 1.8,  .64, 1)
 *   power3.inOut  ≈ cubic-bezier(.65, 0, .35, 1)
 *
 * ---------------------------------------------------------------------
 * DVĚ FÁZE
 * ---------------------------------------------------------------------
 * 1. NÁLET. Dvanáct dlaždic přilétne z okrajů (směr per slot), zmenšené na
 *    polovinu, pootočené o 16° proti klidové rotaci a rozostřené. Čas
 *    `t = 0,05 + i · 0,10 s`, doba 0,42 s — čísla z kompozice.
 *
 * 2. ZOOM. Showreel zvětší tři dlaždice s klipem (`ZOOM=[1,6,7]`) v čase
 *    1,85 / 2,55 / 3,25 s. Na webu z toho je SMYČKA přes všech sedm klipů
 *    v klidném rytmu; po kole pauza. Dlaždice ZŮSTÁVÁ NA MÍSTĚ,
 *    jen se narovná na 0°, zvětší 1,65× a jde nad ostatní — přesně jako
 *    `zoom()` v kompozici, žádný přesun do středu.
 *
 * ---------------------------------------------------------------------
 * ČTYŘI VĚCI, KTERÉ SE LIŠÍ OD KOMPOZICE, A PROČ
 * ---------------------------------------------------------------------
 * 1. OFFSETY JSOU V PROCENTECH PLÁTNA, NE PEVNÝCH ±1100 / ±820 px.
 *    Kompozice je psaná pro plátno 1920 × 1080. Na 1440 by pevné pixely
 *    startovaly dál, než je potřeba, na 2560 zase uvnitř obrazu a „nálet
 *    zvenčí" by byl vidět jako zjevení uprostřed. ±57 % šířky a ±76 %
 *    výšky plátna je týž poměr jako v návrhu.
 *
 * 2. ZOOM JE SMYČKA, NE TŘI JEDNORÁZOVÉ ZÁBLESKY. Showreel má 4,4 s
 *    a skončí; stránka žije dál. Majitel: „postupně jednotlivé polaroidy
 *    vyzoomují a zase zalezou".
 *
 * 3. BLUR SE PO DOBĚHU NEDRŽÍ. Gotcha z karty efektu: zbytkový `filter`
 *    drží prvek na vlastní kompozitní vrstvě a obraz je pak viditelně hůř
 *    vyhlazený. `fill: 'backwards'` znamená, že po doběhu už animace
 *    nepřispívá ničím — ověřeno měřením, ne předpokladem.
 *
 * 4. KLIPY SE PŘEHRÁVAJÍ JEN VE VÝHLEDU. `preload="none"` + start až při
 *    vstupu sekce do viewportu. Sedm současně běžících videí mimo obraz
 *    je zbytečná zátěž na dekodér i na baterii.
 *
 * ---------------------------------------------------------------------
 * KDY SE NEDĚJE NIC
 * ---------------------------------------------------------------------
 * `prefers-reduced-motion: reduce` — ani nálet, ani zoom, ani přehrávání.
 * Dlaždice jsou rovnou na místě a klipy ukazují plakát. Pohyb dvanácti
 * objektů přes celou obrazovku je přesně to, co ta předvolba vypíná.
 *
 * Pod 1070 px se nespustí nic: sekce je `display: none` a média se díky
 * `preload="none"` a `loading="lazy"` ani nestahují.
 *
 * ---------------------------------------------------------------------
 * PAUZA (WCAG 2.2.2)
 * ---------------------------------------------------------------------
 * Smyčka zoomů běží déle než pět vteřin a spouští se sama, takže musí jít
 * zastavit ovládacím prvkem — `mouseenter` se nepočítá, klávesnice ani
 * dotyk ho nevyvolají (SO-19 bod 1). Tlačítko odkrývá AŽ TENHLE SOUBOR
 * a jen ve chvíli, kdy smyčku opravdu zakládá: v klidovém režimu, pod
 * prahem desktopu ani bez JavaScriptu se nic nehýbe a ovládání pauzy by
 * nad nehybným obrazem jen mátlo.
 *
 * Pauza NEČEKÁ na doběh rozjeté animace. `getAnimations().cancel()`
 * vrátí dlaždici do klidového stavu okamžitě — „zastav pohyb" znamená
 * teď, ne za 1,8 s, a měřitelné je to stejně: po stisku se transformy
 * nesmí hnout ani o pixel.
 */

/** Práh desktopu. Shodný s `efekty.css` i s ostatními bloky šablony. */
const DESKTOP = '(min-width: 1070px)';

/*
 * VŠECHNY DOBY NA JEDNOM MÍSTĚ. Majitel je po každé prohlídce ladí, takže
 * musí jít změnit jednou hodnotou, ne hledáním čísla v kódu.
 */

/* Nálet — čísla z kompozice showreelu, beze změny. */
const NALET_DOBA = 420;
const NALET_ROZESTUP = 100;
const NALET_KRIVKA = 'cubic-bezier(.34, 1.56, .64, 1)';

/* Zoom — tvar z `zoom()` v kompozici. */
const ZOOM_VEN = 300;
const ZOOM_VYDRZ = 1200;
const ZOOM_ZPET = 340;
const ZOOM_MERITKO = 1.65;
const ZOOM_KRIVKA_VEN = 'cubic-bezier(.34, 1.8, .64, 1)';
const ZOOM_KRIVKA_ZPET = 'cubic-bezier(.65, 0, .35, 1)';

/*
 * RYTMUS. Kompozice má mezi zoomy 0,70 s, jenže to je střih pro
 * čtyřapůlvteřinový spot. Majitel po prohlídce DEV: „nastav tam nějaký
 * rozumný timing pro ty zoomy" — na stránce, kde člověk čte, je klidný
 * interval 3 s. `ZOOM_PRODLEVA` je celý interval mezi STARTY dvou zoomů,
 * ne mezera po doběhu: jinak by se rytmus měnil s dobou animace.
 */
const ZOOM_PRODLEVA = 3000;
const ZOOM_PRVNI = 1500;
const ZOOM_PAUZA_PO_KOLE = 2500;

/*
 * SMĚR NÁLETU PODLE POŘADÍ SLOTU, opsaný z pole `P` v kompozici.
 * Pozice slotů drží `efekty.css`, směr potřebuje jen animace.
 */
const SMERY = ['T', 'T', 'T', 'T', 'L', 'L', 'R', 'R', 'B', 'B', 'B', 'B'];

/**
 * Odkud dlaždice přilétá. Kompozice má pevných ±1100 / ±820 px na plátně
 * 1920 × 1080; tady je to týž poměr v procentech skutečného plátna.
 */
function odkud(smer, sirka, vyska) {
	const dx = Math.round(sirka * 0.573);
	const dy = Math.round(vyska * 0.759);
	switch (smer) {
		case 'L': return `translate(${-dx}px, 0)`;
		case 'R': return `translate(${dx}px, 0)`;
		case 'T': return `translate(0, ${-dy}px)`;
		default: return `translate(0, ${dy}px)`;
	}
}

/** Klidová rotace slotu. Jediný zdroj je CSS, ať se to nerozejde. */
function rotace(el) {
	return window.getComputedStyle(el).getPropertyValue('--plr-rot').trim() || '0deg';
}

export function init() {
	const platna = [...document.querySelectorAll('[data-plr]')];
	if (!platna.length) return 0;

	const klid = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
	const desktop = !!(window.matchMedia && window.matchMedia(DESKTOP).matches);

	// Pod prahem se nedělá vůbec nic: sekce je skrytá a nic se nestahuje.
	if (!desktop) return 0;

	/*
	 * PLAKÁTY SE NASAZUJÍ AŽ TADY, A JE TO OPRAVA MĚŘENÉHO NÁLEZU.
	 *
	 * `preload="none"` zastaví stahování videa, ale NE plakátu — ten si
	 * prohlížeč stáhne zvlášť, protože je to první snímek, který má
	 * ukázat. Brána bodu P na 390 px napočítala sedm požadavků na obrázky
	 * ve skryté sekci. Markup proto nese `data-poster` a atribut `poster`
	 * vzniká až tady, tedy jen nad 1070 px.
	 *
	 * Děje se to PŘED klidovou větví schválně: v `prefers-reduced-motion`
	 * se nepřehrává, takže plakát je jediné, co v dlaždici bude vidět.
	 */
	for (const v of document.querySelectorAll('.plr video[data-poster]')) {
		v.poster = v.dataset.poster;
	}

	/*
	 * V klidovém režimu se nenasazuje ani `.plr-ceka`. Dlaždice tím
	 * zůstanou ve svém klidovém stavu, což je přesně ten, na kterém mají
	 * po animaci skončit.
	 */
	if (klid) return 0;

	let pocet = 0;

	for (const platno of platna) {
		if (platno.dataset.za2026Plr === '1') continue;
		const dlazdice = [...platno.querySelectorAll('.plr-tile')];
		if (!dlazdice.length) continue;
		platno.dataset.za2026Plr = '1';
		pocet += 1;

		const klipy = dlazdice.filter((el) => el.querySelector('video'));
		platno.classList.add('plr-ceka');

		let casovac = null;
		let vHledu = false;
		let podMysi = false;
		let pauza = false;
		let naRade = 0;

		const zastav = () => {
			if (casovac) { window.clearTimeout(casovac); casovac = null; }
		};

		/* ---------------------------------------------------------------
		 * PŘEHRÁVÁNÍ KLIPŮ
		 * ------------------------------------------------------------- */
		const prehravej = (ano) => {
			for (const el of klipy) {
				const v = el.querySelector('video');
				if (!v) continue;
				if (ano) {
					/*
					 * `play()` vrací slib, který se na starších enginech
					 * odmítne, když zdroj ještě není. Odmítnutí se spolkne
					 * schválně: nepřehrané video ukáže plakát a stránka
					 * kvůli tomu nemá házet chybu do konzole.
					 */
					const slib = v.play();
					if (slib && slib.catch) slib.catch(() => {});
				} else {
					v.pause();
				}
			}
		};

		/* ---------------------------------------------------------------
		 * FÁZE 1 — NÁLET
		 * ------------------------------------------------------------- */
		const nalet = () => {
			platno.classList.remove('plr-ceka');
			const r = platno.getBoundingClientRect();

			dlazdice.forEach((el, i) => {
				const rot = rotace(el);
				el.animate(
					[
						{
							opacity: 0,
							filter: 'blur(10px)',
							transform: `${odkud(SMERY[i % SMERY.length], r.width, r.height)} scale(.5) rotate(calc(${rot} - 16deg))`,
						},
						{
							opacity: 1,
							filter: 'blur(0px)',
							transform: `translate(0, 0) scale(1) rotate(${rot})`,
						},
					],
					{
						duration: NALET_DOBA,
						delay: 50 + i * NALET_ROZESTUP,
						easing: NALET_KRIVKA,
						fill: 'backwards',
					}
				);
			});

			return 50 + dlazdice.length * NALET_ROZESTUP + NALET_DOBA;
		};

		/* ---------------------------------------------------------------
		 * FÁZE 2 — ZOOM VE SMYČCE
		 * ------------------------------------------------------------- */
		const zoom = (el) => {
			const rot = rotace(el);

			/*
			 * `z-index` se mění TŘÍDOU, ne inline stylem: dlaždice musí jít
			 * nad ostatní po celou dobu obou animací a vrátit se, až návrat
			 * doběhne. Kdyby se přepisoval `style`, přetloukl by ho slot.
			 */
			el.classList.add('plr-vpredu');

			const ven = el.animate(
				[
					{ transform: `scale(1) rotate(${rot})` },
					{ transform: `scale(${ZOOM_MERITKO}) rotate(0deg)` },
				],
				{ duration: ZOOM_VEN, easing: ZOOM_KRIVKA_VEN, fill: 'forwards' }
			);

			ven.finished.then(() => {
				window.setTimeout(() => {
					const zpet = el.animate(
						[
							{ transform: `scale(${ZOOM_MERITKO}) rotate(0deg)` },
							{ transform: `scale(1) rotate(${rot})` },
						],
						{ duration: ZOOM_ZPET, easing: ZOOM_KRIVKA_ZPET }
					);
					// `ven` se ruší až tady, jinak by dlaždice mezi oběma
					// animacemi na jeden snímek skočila zpátky do klidu.
					zpet.ready.then(() => ven.cancel());
					zpet.finished.then(() => el.classList.remove('plr-vpredu')).catch(() => {});
				}, ZOOM_VYDRZ);
			}).catch(() => {
				// Zrušená animace (odchod z výhledu) není chyba.
				el.classList.remove('plr-vpredu');
			});
		};

		/*
		 * JEDEN ČASOVAČ, PEVNÝ INTERVAL.
		 *
		 * Majitel při prohlídce DEV: „teď se dlouho nic neděje, pak jeden
		 * zoom, zase dlouho nic neděje a pak se to zoomuje pravidelně".
		 * Měl pravdu a příčina byla v plánování: nálet a zoom měly každý
		 * vlastní frontu, `visibilitychange` i odjetí myši volaly vstup
		 * znovu a naplánovaly druhý běh vedle prvního. Výsledkem byly
		 * shluky a díry.
		 *
		 * Teď je jediný časovač a jediná cesta, jak ho založit. Každý start
		 * zoomu je o `ZOOM_PRODLEVA` od předchozího; po kole se jednou
		 * přidá `ZOOM_PAUZA_PO_KOLE`. Nic nečeká na `canplay` — klip se
		 * rozjede, až se načte, a do té doby je v dlaždici plakát.
		 */
		const kolo = () => {
			casovac = null;
			if (!vHledu || podMysi || pauza || document.hidden || !klipy.length) return;

			zoom(klipy[naRade]);
			naRade += 1;

			const dokolecka = naRade >= klipy.length;
			if (dokolecka) naRade = 0;
			casovac = window.setTimeout(kolo, ZOOM_PRODLEVA + (dokolecka ? ZOOM_PAUZA_PO_KOLE : 0));
		};

		const naplanuj = (za) => {
			if (casovac || !vHledu || podMysi || pauza || document.hidden) return;
			casovac = window.setTimeout(kolo, za);
		};

		/* ---------------------------------------------------------------
		 * SPOUŠTĚNÍ
		 * ------------------------------------------------------------- */
		let naletlo = false;

		const vstup = () => {
			if (pauza) return;
			prehravej(true);
			if (naletlo) { naplanuj(ZOOM_PRODLEVA); return; }
			naletlo = true;
			const doba = nalet();
			naplanuj(doba + ZOOM_PRVNI);
		};

		const odchod = () => {
			prehravej(false);
			zastav();
		};

		/*
		 * PAUZA JEN PŘI NAJETÍ NA DLAŽDICI, NE NA CELÉ PLÁTNO. Plátno je
		 * skoro přes celou šířku obsahu, takže kurzor odložený kdekoli
		 * v sekci by smyčku zastavil a majitel by viděl „nic se neděje".
		 */
		for (const el of dlazdice) {
			el.addEventListener('mouseenter', () => { podMysi = true; zastav(); });
			el.addEventListener('mouseleave', () => { podMysi = false; naplanuj(ZOOM_PRODLEVA); });
		}

		/* ---------------------------------------------------------------
		 * TLAČÍTKO PAUZA / PŘEHRÁT (WCAG 2.2.2)
		 * ------------------------------------------------------------- */
		const tlacitko = (platno.closest('.plr') || document).querySelector('[data-plr-pauza]');

		if (tlacitko) {
			// Odkrývá se až tady: dokud smyčka neběží, nemá co ovládat.
			tlacitko.hidden = false;

			const popis = (stisknuto) => {
				const t = stisknuto ? tlacitko.dataset.plrTextPrehrat : tlacitko.dataset.plrTextPauza;
				if (t) tlacitko.textContent = t;
				tlacitko.setAttribute('aria-pressed', stisknuto ? 'true' : 'false');
			};

			tlacitko.addEventListener('click', () => {
				pauza = !pauza;
				popis(pauza);

				if (!pauza) {
					if (vHledu) vstup();
					return;
				}

				/*
				 * ZASTAVIT ZNAMENÁ TEĎ. `odchod()` sundá klipy a zruší
				 * časovač, ale rozjetý zoom by dobíhal další necelé dvě
				 * vteřiny — a to je pořád pohyb, který uživatel zastavil.
				 * `cancel()` vrátí dlaždici do klidového stavu z CSS;
				 * `fill: 'forwards'` u zvětšení tím přestane přispívat.
				 */
				odchod();
				for (const el of dlazdice) {
					if (el.getAnimations) {
						for (const a of el.getAnimations()) a.cancel();
					}
					el.classList.remove('plr-vpredu');
				}
			});
		}

		document.addEventListener('visibilitychange', () => {
			if (document.hidden) odchod();
			else if (vHledu) vstup();
		});

		if (!window.IntersectionObserver) {
			// Bez pozorovatele je lepší mít sekci hotovou než neviditelnou.
			vHledu = true;
			vstup();
			continue;
		}

		const pozorovatel = new window.IntersectionObserver(
			(zaznamy) => {
				for (const z of zaznamy) {
					vHledu = z.isIntersecting;
					if (vHledu) vstup();
					else odchod();
				}
			},
			{ threshold: 0.35 }
		);
		pozorovatel.observe(platno);
	}

	return pocet;
}
